import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { api, ApiError, type Me, type InboxItem } from './api';
import { Shell } from './components/Shell';
import { SignIn } from './pages/SignIn';
import { Register } from './pages/Register';
import { DocumentDetail } from './pages/DocumentDetail';
import { Inbox } from './pages/Inbox';
import { Overdue } from './pages/Overdue';
import { MODULE, meta, type ModuleKey } from './module';
import { ProgRoutes } from './pages/prog/routes';
import { CostRoutes } from './pages/cost/routes';
import { QaRoutes } from './pages/qa/routes';
import { DelayRoutes } from './pages/delay/routes';
import { TeamRoutes } from './pages/team/routes';
import { ProcRoutes } from './pages/proc/routes';
import { CxRoutes } from './pages/cx/routes';
import { Preview } from './preview/Preview';
import { Loading, ErrorState } from './components/ui';

type Auth = { status: 'checking' } | { status: 'out' } | { status: 'in'; me: Me } | { status: 'error'; message: string };

/**
 * The preview is decided before anything else, including the auth
 * call. It reads no API, so waiting on a session it does not need
 * would only make the mockup flash a spinner for no reason.
 */
const IS_PREVIEW = location.pathname.replace(/\/+$/, '') === '/preview';

export function App() {
  const [auth, setAuth] = useState<Auth>({ status: 'checking' });
  const [inboxCount, setInboxCount] = useState(0);

  const check = useCallback(async () => {
    if (IS_PREVIEW) return;
    try {
      const me = await api.get<Me>('/auth/me');
      setAuth({ status: 'in', me });
    } catch (e) {
      // 401 is the normal signed-out path, not an error to shout
      // about. Anything else genuinely went wrong and should say so
      // rather than silently showing a login form.
      if (e instanceof ApiError && e.status === 401) setAuth({ status: 'out' });
      else setAuth({ status: 'error', message: e instanceof ApiError ? e.message : 'Unknown error' });
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  // One build serves both subdomains, so the tab title has to follow
  // the module rather than being baked into index.html.
  useEffect(() => {
    document.title = `${meta.title} — Munin Partners`;
  }, []);

  useEffect(() => {
    if (auth.status !== 'in') return;
    api
      .get<{ items: InboxItem[] }>('/inbox')
      .then((r) => setInboxCount(r.items.length))
      .catch(() => undefined);
  }, [auth.status]);

  if (IS_PREVIEW) return <Preview />;

  if (auth.status === 'checking') {
    return (
      <div className="wrap page">
        <Loading rows={3} />
      </div>
    );
  }

  if (auth.status === 'error') {
    return (
      <div className="wrap page">
        <ErrorState message={auth.message} onRetry={check} />
      </div>
    );
  }

  if (auth.status === 'out') return <SignIn onSignedIn={check} />;

  // A module that is not built must not fall through to another
  // module's register — PM's documents under the HSE banner would be
  // worse than showing nothing. It gets the mockup instead of a wall:
  // signing in and being told "nothing here" is the least useful
  // thing this platform could do with a real account.
  if (!meta.live) return <Preview me={auth.me} />;

  // Only pm. and eng. are document registers. Every other module is a
  // different product on the same shell, so each brings its own route
  // set and they are looked up rather than chained through a ternary.
  //
  // The lookup is what matters, not the tidiness. Chained ternaries
  // ended in an `else` that rendered the document register, so a
  // module marked live before its routes existed did not fail — it
  // quietly served PM's documents under its own banner. proc. and cx.
  // shipped in exactly that state. A missing entry here now falls to
  // the mockup instead, which is wrong in a way somebody notices.
  const DocumentRoutes = () => (
    <Routes>
      <Route path="/" element={<Register />} />
      <Route path="/documents/:id" element={<DocumentDetail myUserId={auth.me.user.id} />} />
      <Route path="/inbox" element={<Inbox onCountChange={setInboxCount} />} />
      {meta.usesStages && <Route path="/overdue" element={<Overdue />} />}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  const BY_MODULE: Partial<Record<ModuleKey, () => JSX.Element>> = {
    PM: DocumentRoutes,
    ENG: DocumentRoutes,
    PROC: ProcRoutes,
    PROG: ProgRoutes,
    CX: CxRoutes,
    COSTM: CostRoutes,
    QM: QaRoutes,
    DELAY: DelayRoutes,
    TEAM: TeamRoutes,
  };

  const ModuleRoutes = BY_MODULE[MODULE];
  if (!ModuleRoutes) return <Preview me={auth.me} />;

  return (
    <BrowserRouter>
      <Shell me={auth.me} inboxCount={inboxCount}>
        <ModuleRoutes />
      </Shell>
    </BrowserRouter>
  );
}
